/**
 * ChangePasswordForm - An organism component for changing user password.
 *
 * Provides a form with current password, new password, and confirmation fields.
 * Handles client-side validation and displays server-side errors.
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';
import { InlineError } from '@lean-agility/design-system/react';
import { PasswordInput } from '../molecules/PasswordInput';
import { changePasswordSchema, type ChangePasswordFormData } from '../../validation/authSchemas';

export interface ChangePasswordFormProps {
  /**
   * Callback invoked when the form is successfully submitted.
   * Receives the current and new password.
   */
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;

  /**
   * Whether the form is in a loading/submitting state.
   * @default false
   */
  isLoading?: boolean;

  /**
   * Whether the password was changed successfully.
   * Shows a success message instead of the form.
   * @default false
   */
  isSuccess?: boolean;

  /**
   * Error message to display above the form.
   * Typically used for API-level errors.
   */
  error?: string | null;

  /**
   * Callback to dismiss the error message.
   */
  onDismissError?: () => void;

  /**
   * Callback invoked when cancel is clicked.
   */
  onCancel?: () => void;
}

export function ChangePasswordForm({
  onSubmit,
  isLoading = false,
  isSuccess = false,
  error,
  onDismissError,
  onCancel,
}: ChangePasswordFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const handleFormSubmit = async (data: ChangePasswordFormData) => {
    await onSubmit(data.currentPassword, data.newPassword);
  };

  const isFormLoading = isLoading || isSubmitting;

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <CheckCircle className="size-10 text-feedback-success" />
        <p className="text-sm font-medium text-text-primary">Password changed successfully</p>
        <p className="text-xs text-text-muted">Your new password is now active.</p>
        {onCancel && (
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} className="mt-2">
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <div className="space-y-4">
        {error && <InlineError error={error} dismissible onDismiss={onDismissError} />}

        <PasswordInput
          {...register('currentPassword')}
          label="Current password"
          placeholder="Enter your current password"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          disabled={isFormLoading}
          autoFocus
        />

        <PasswordInput
          {...register('newPassword')}
          label="New password"
          placeholder="Enter your new password"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          hint="Must be at least 8 characters"
          disabled={isFormLoading}
        />

        <PasswordInput
          {...register('confirmPassword')}
          label="Confirm new password"
          placeholder="Re-enter your new password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          disabled={isFormLoading}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={isFormLoading}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="primary" size="md" disabled={isFormLoading}>
            {isFormLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Changing...
              </>
            ) : (
              'Change password'
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
